import { type AnimationEvent, useEffect, useState } from "react";

/**
 * How long to wait for a panel that never reports back before taking it away
 * regardless. Deliberately far longer than any leaving animation: this is the
 * net under the wire, not a second copy of the duration.
 */
const ABANDON_AFTER_MS = 1000;

/**
 * Keeps an overlay on screen for as long as it takes to leave.
 *
 * Arriving needs no help: a CSS animation runs from the frame an element is
 * mounted. Leaving does, because by then the component rendering it has
 * already stopped. This holds it in the tree until it says it has gone.
 *
 * The panel reports that itself, through `onLeft`, rather than being given a
 * duration to match — a number in the stylesheet and the same number in a
 * component drift apart the first time one of them is tuned.
 *
 * ```tsx
 * const { mounted, leaving, onLeft } = usePanelPresence(open);
 * return mounted ? <Panel leaving={leaving} onAnimationEnd={onLeft} /> : null;
 * ```
 */
export function usePanelPresence(open: boolean) {
  const [mounted, setMounted] = useState(open);

  // Catching up during the render rather than in an effect, so an overlay
  // that has just been asked for is in the tree on the very next paint
  // instead of the one after it.
  if (open && !mounted) setMounted(true);

  const leaving = mounted && !open;

  // An animation only runs while the page is drawing frames, so a tab that
  // goes away mid-flight never reaches the end of one — and neither does a
  // panel whose animation someone has turned off. Either way what is left
  // covering the page is a scrim that nothing can get past.
  useEffect(() => {
    if (!leaving) return;
    const abandon = setTimeout(() => setMounted(false), ABANDON_AFTER_MS);
    return () => clearTimeout(abandon);
  }, [leaving]);

  return {
    mounted,
    leaving,

    /**
     * Put on the element carrying the leaving animation. Anything inside it
     * that animates ends too, so only the element itself is listened to.
     */
    onLeft: (event: AnimationEvent<HTMLElement>) => {
      if (!open && event.target === event.currentTarget) setMounted(false);
    },
  };
}
